import { Directive, ElementRef, HostListener, Input, AfterViewChecked } from '@angular/core';

@Directive({
  selector: '[appNumericInput]'
})
export class NumericInputDirective implements AfterViewChecked {
  /**
   * By default allow decimal separator. Set to false when only integers are allowed.
   */
  @Input('appNumericInputAllowDecimal') allowDecimal = true;

  /** When true, displays the value with space-separated thousands while the input is not focused. */
  @Input('appNumericInputThousandSep') thousandSep = false;

  constructor(private host: ElementRef) { }

  private get input(): HTMLInputElement {
    return this.host.nativeElement as HTMLInputElement;
  }

  ngAfterViewChecked(): void {
    if (!this.thousandSep) return;
    if (document.activeElement === this.input) return;

    const raw = this.input.value.replace(/\s/g, '');
    const formatted = this.formatThousands(raw);
    if (this.input.value !== formatted) {
      this.input.value = formatted;
    }
  }

  @HostListener('focus')
  onFocus(): void {
    if (this.thousandSep) {
      const raw = this.input.value.replace(/\s/g, '');
      if (this.input.value !== raw) {
        this.input.value = raw;
      }
    }
  }

  @HostListener('input')
  onInput(): void {
    const rawValue = this.input.value;
    const sanitized = this.sanitizeValue(rawValue);

    if (sanitized !== rawValue) {
      const position = this.host.nativeElement.selectionStart ?? sanitized.length;
      this.host.nativeElement.value = sanitized;
      this.host.nativeElement.setSelectionRange(position, position);
      this.host.nativeElement.dispatchEvent(new Event('input'));
    }
  }

  @HostListener("blur")
  onBlur(): void {
    if (this.input.value === "") {
      this.input.value = "0";
      this.input.dispatchEvent(new Event("input"));
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    const text = event.clipboardData?.getData('text') || '';
    if (text) {
      const sanitized = this.sanitizeValue(text);
      if (sanitized !== text) {
        event.preventDefault();
        const start = this.host.nativeElement.selectionStart ?? 0;
        const end = this.host.nativeElement.selectionEnd ?? 0;
        const current = this.host.nativeElement.value;
        this.host.nativeElement.value = current.slice(0, start) + sanitized + current.slice(end);
        const caret = start + sanitized.length;
        this.host.nativeElement.setSelectionRange(caret, caret);
        this.host.nativeElement.dispatchEvent(new Event('input'));
      }
    }
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const allowedControlKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End'
    ];

    if (allowedControlKeys.includes(event.key)) {
      return;
    }

    if ((event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x', 'z'].includes(event.key.toLowerCase())) {
      return;
    }

    const isDigit = /^\d$/.test(event.key);
    const isDecimal = this.allowDecimal && (event.key === '.' || event.key === ',');
    const isMinus = event.key === '-' && this.host.nativeElement.selectionStart === 0 && !this.host.nativeElement.value.includes('-');

    if (!isDigit && !isDecimal && !isMinus) {
      event.preventDefault();
    }
  }

  private sanitizeValue(value: string): string {
    let sanitized = value.replace(/\s+/g, '');
    sanitized = sanitized.replace(',', '.');
    sanitized = sanitized.replace(/[^0-9.-]/g, '');

    if (!this.allowDecimal) {
      sanitized = sanitized.replace(/\./g, '');
    } else {
      const firstDotIndex = sanitized.indexOf('.');
      if (firstDotIndex !== -1) {
        const before = sanitized.slice(0, firstDotIndex + 1);
        const after = sanitized
          .slice(firstDotIndex + 1)
          .replace(/\./g, '');
        sanitized = `${before}${after}`;
      }
    }

    const negativeMatches = sanitized.match(/-/g) || [];
    if (negativeMatches.length > 1) {
      sanitized = sanitized.replace(/-/g, '');
    }

    if (negativeMatches.length === 1 && !sanitized.startsWith('-')) {
      sanitized = '-' + sanitized.replace(/-/g, '');
    }

    return sanitized;
  }

  private formatThousands(value: string): string {
    if (!value || value === '0' || value === '-') return value;

    const parts = value.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return parts.join('.');
  }
}



// WEBPACK FOOTER //
// ./src/app/directives/numeric-input.directive.ts